// ─────────────────────────────────────────────────────────────────────────────
// Clinic money, bound to a currency.
//
// A clinic keeps its books in ONE currency (docs/clinic-currency/CONTRACTS.md):
// IRR for every clinic that existed before the choice did, and whatever an owner
// picked for a clinic created since. Every amount on the wire is an integer
// number of MINOR UNITS of its document's currency — whole rials for IRR.
//
// `bindMoney` takes the plain i18n value (`t`, `formatMoney`, …) and returns the
// money half of it bound to one currency, so a screen never has to ask which
// currency it is in:
//
//   const { formatMoney, moneyUnit, t } = useTranslation();
//   formatMoney(invoice.totalRial)                          // the clinic's currency
//   formatMoney(invoice.totalRial, { currency: invoice.currency })  // the document's
//   moneyUnit()            // «تومان» / «US dollar»
//   moneyUnit('short')     // «ت» / «USD»
//   t('cashier.deposit.amount')  // «مبلغ ({{unit}})» → «مبلغ (تومان)» / «مبلغ (US dollar)»
//   t(key, { amount, ...moneyUnits(doc.currency) })  // a document's own unit words
//
// The rules it keeps:
//
//   • IRR is byte-identical to before currencies existed. Its unit word comes from
//     the app's OWN catalogue (`common.units.toman` / `tomanShort`), never from the
//     currency table, so English still says «Toman» and «T».
//   • A blank `currency` option means THE BOUND currency (the clinic's), not IRR:
//     a document whose query did not select `currency` is, in practice, in the
//     clinic's currency — a clinic cannot change currency once money exists. The
//     BFF already turned a blank wire currency into 'IRR' before it got here.
//   • No conversion anywhere. Two currencies are never added or compared.
//   • Platform money — the subscription, پارسه, SMS/WhatsApp prices — is not
//     clinic money: it keeps its literal toman words and never passes through here.
// ─────────────────────────────────────────────────────────────────────────────

import {
  DEFAULT_CURRENCY, isUnknownCurrency, reportUnknownCurrency,
  toDisplay, fromDisplay, parseAmount, unitLabel,
} from '../lib/currency';
import { FALLBACK, MONEY_UNIT_KEYS } from './translator';

export { DEFAULT_CURRENCY };

/**
 * The clinic's currency as the app should use it: blank → IRR, anything else
 * AS SENT.
 *
 * A code this build does not know (a server ahead of the client) is KEPT, never
 * folded to IRR — that would print a tenth of an unknown amount under «تومان»
 * (CONTRACTS §0). It is reported once, and the rest of the app reads it as what
 * is known about it: raw minor units and the code, with money entry disabled
 * until the app is updated (lib/currency.js).
 */
export function clinicCurrencyOf(code) {
  if (code == null || code === '') return DEFAULT_CURRENCY;
  if (isUnknownCurrency(code)) reportUnknownCurrency(code);
  return code;
}

/**
 * `vars` with each default added only where the caller did not name it — a
 * caller's own value always wins, even an explicit null.
 */
function withDefaults(vars, defaults) {
  if (vars == null) return { ...defaults };
  let out = null;
  for (const key of Object.keys(defaults)) {
    if (!Object.prototype.hasOwnProperty.call(vars, key)) {
      if (!out) out = { ...vars };
      out[key] = defaults[key];
    }
  }
  return out || vars;
}

/**
 * The money helpers of an i18n value, bound to `currency`.
 *
 * `base` is an i18n context value (`t`, `tList`, `formatMoney`, `locale`); its
 * `formatMoney` reads a blank currency as IRR, which is why every call here
 * resolves the currency first.
 */
export function bindMoney(base, currency = DEFAULT_CURRENCY) {
  // Never normalised into a throw: an unknown code is kept, and every helper
  // below reads it through lib/currency.js as raw minor units and its code.
  const bound = currency == null || currency === '' ? DEFAULT_CURRENCY : currency;
  const resolve = (code) => (code == null || code === '' ? bound : code);

  /**
   * The unit word beside a figure: `moneyUnit()` long, `moneyUnit('short')`,
   * `moneyUnit('short', doc.currency)` for a document in its own currency.
   * `'inline'` is the long word as English writes it mid-sentence («toman»);
   * every other currency's long word is already that. `'field'` is the chip on a
   * MoneyInput: the long catalogue word for IRR, as every such chip has always
   * read, and the ISO code otherwise — what the kit's own chip shows for them.
   */
  const moneyUnit = (style = 'long', code) => {
    const c = resolve(code);
    if (c === DEFAULT_CURRENCY) return base.t(MONEY_UNIT_KEYS[style] || MONEY_UNIT_KEYS.long);
    return unitLabel(c, base.locale, style === 'short' || style === 'field' ? 'short' : 'long');
  };

  /** The `t` defaults for a document in `code` — `t(key, { ...unitsOf(doc.currency) })`. */
  const unitsOf = (code) => ({
    unit: moneyUnit('long', code),
    unitInline: moneyUnit('inline', code),
    unitShort: moneyUnit('short', code),
  });

  const unitDefaults = unitsOf();
  // A STRING second argument is the caller's fallback text (translator.js), not interpolation values:
  // spreading it into the defaults would turn «in_visit» into `{0: 'i', 1: 'n', …}` and lose it.
  const t = (key, vars) => (typeof vars === 'string'
    ? base.t(key, { ...unitDefaults, [FALLBACK]: vars })
    : base.t(key, withDefaults(vars, unitDefaults)));
  const tList = (key, vars) => base.tList(key, withDefaults(vars, unitDefaults));

  /** Minor units → the figure; `{ currency }` for a document in its own. */
  const formatMoney = (amountMinor, options = {}) =>
    base.formatMoney(amountMinor, { ...options, currency: resolve(options?.currency) });

  /**
   * «۵۰۰٬۰۰۰ ت» / «12.50 USD» — a figure and its short unit, through the one
   * catalogue string that has always joined them (`common.money.tomanAmount`,
   * whose name predates currencies).
   */
  const moneyAmount = (amountMinor, { currency: code } = {}) => base.t('common.money.tomanAmount', {
    amount: formatMoney(amountMinor, { currency: code }),
    unitShort: moneyUnit('short', code),
  });

  return {
    currency: bound,
    t,
    tList,
    formatMoney,
    moneyUnit,
    moneyUnits: unitsOf,
    moneyAmount,
    /** Minor units → display units (IRR: rials → toman); null when blank. */
    toDisplayAmount: (amountMinor, code) => toDisplay(amountMinor, resolve(code)),
    /** A display figure or a MoneyInput's bare string → minor units (blank → 0). */
    fromDisplayAmount: (displayValue, code) => fromDisplay(displayValue, resolve(code)),
    /** What a human typed → minor units, NaN when it is not an amount (blank too). */
    parseMoney: (text, code) => parseAmount(text, resolve(code)),
  };
}
